/**
 * Supabase auth links (signup confirmation, password recovery, magic link)
 * redirect back to the site URL when they fail — e.g. an expired one-time
 * link lands on `/` with a hash like:
 *
 *   #error=access_denied&error_code=otp_expired&error_description=Email+link+is+invalid+or+has+expired
 *
 * Without handling, the user silently lands on the marketing page with a
 * scary URL and no explanation (backlog AUTH-13). `readAuthLinkError`
 * surfaces that state so we can route the user to a friendly "link expired"
 * screen instead.
 *
 * A post-redirect OAuth failure (consent denied, an unverified-email
 * linking conflict, a not-yet-configured provider) lands back on `/` with
 * this exact same `#error=...` hash shape, since `signInWithOAuth`'s own
 * `redirectTo` is also the site root (backlog AUTH-16) — the error code
 * alone can't reliably tell the two apart, so `markOAuthRedirectStarted()`
 * leaves an explicit same-tab marker right before the redirect fires,
 * checked (and cleared) here instead.
 */
const OAUTH_REDIRECT_MARKER_KEY = "snapdraw.oauthRedirectStarted";

/** Call immediately before `supabase.auth.signInWithOAuth(...)` redirects the page away. */
export function markOAuthRedirectStarted(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(OAUTH_REDIRECT_MARKER_KEY, "1");
}

/**
 * Call if `signInWithOAuth(...)` returns a synchronous error instead of
 * actually redirecting — otherwise the marker set above would linger in
 * sessionStorage for the rest of the tab's life and wrongly attribute a
 * later, unrelated auth-link error (e.g. a real expired confirmation link
 * opened in the same tab) to OAuth.
 */
export function clearOAuthRedirectMarker(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(OAUTH_REDIRECT_MARKER_KEY);
}

export type AuthLinkError = {
  /** Supabase error code, e.g. "otp_expired", or the bare error ("access_denied"). */
  code: string;
  /** Human-readable description from Supabase (URL-decoded). */
  description: string;
  /** True when this error followed a signInWithOAuth() redirect, not an expired confirmation/recovery link — same hash shape, a different cause that needs different copy. */
  fromOAuth: boolean;
} | null;

export function readAuthLinkError(): AuthLinkError {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw.includes("error")) return null;
  const params = new URLSearchParams(raw);
  const code = params.get("error_code") ?? params.get("error");
  if (!code) return null;
  const fromOAuth = window.sessionStorage.getItem(OAUTH_REDIRECT_MARKER_KEY) === "1";
  window.sessionStorage.removeItem(OAUTH_REDIRECT_MARKER_KEY);
  return { code, description: params.get("error_description") ?? "", fromOAuth };
}

/** Strip the error hash so a refresh doesn't re-trigger the redirect. */
export function clearAuthLinkError(): void {
  if (typeof window === "undefined") return;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}
